import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Github, Twitter, Mail } from 'lucide-react';
import { sisterTools } from '../lib/toolLinks';

const Footer = () => {
  const { t } = useTranslation();

  return (
    <footer
      id="contact"
      className="mt-auto border-t bg-white/90 backdrop-blur-md"
      style={{
        borderColor: 'var(--border)',
        paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))'
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          <div className="col-span-1 md:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex items-center gap-3 mb-4"
            >
              <img src="/logo.svg" alt="" width={32} height={32} className="rounded-md" />
              <span className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                {t('footer.brand')}
              </span>
            </motion.div>
            <p className="mb-4 max-w-md" style={{ color: 'var(--text-secondary)' }}>
              {t('footer.description')}
            </p>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              {t('footer_note')}
            </p>
            <div className="flex gap-4">
              {[Github, Twitter, Mail].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <Icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          <div id="about">
            <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              {t('footer.quickLinks')}
            </h3>
            <ul className="space-y-2">
              {[t('nav.home'), t('nav.games'), t('nav.about'), t('nav.contact')].map((item) => (
                <li key={item}>
                  <a href={`#${item.toLowerCase().replace(/\s/g, '')}`} style={{ color: 'var(--text-secondary)' }}>
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              {t('footer.games')}
            </h3>
            <ul className="space-y-2">
              {[t('games.memory'), t('games.puzzle'), t('games.logic'), t('games.snake'), t('games.tetris')].map((game) => (
                <li key={game}>
                  <a href="#games" style={{ color: 'var(--text-secondary)' }}>
                    {game}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t pt-8 mb-6" style={{ borderColor: 'var(--border)' }}>
          <h3 className="text-lg font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
            {t('moreTools')}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {sisterTools('game').map((tool) => (
              <a
                key={tool.id}
                href={tool.href}
                target="_blank"
                rel="noopener noreferrer"
                className="shell-card p-3 text-sm hover:border-[var(--accent)]"
              >
                <span className="font-bold block" style={{ color: 'var(--text-primary)' }}>{tool.name}</span>
                <span style={{ color: 'var(--text-secondary)' }}>{tool.blurb}</span>
              </a>
            ))}
          </div>
        </div>

        <div className="border-t pt-6 flex flex-col md:flex-row justify-between items-center gap-3" style={{ borderColor: 'var(--border)' }}>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            {t('footer.copyright')}
          </p>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            {t('footer.made')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
